import {
  PAGE_DETAILS,
  ROLE_BY_ROLE_LABEL,
  ROLE_DISPLAY_BY_LABEL,
  ROLE_LABEL_BY_ROLE,
  ROLE_OPTIONS,
} from './userConstants';

/** Vuex key for lb-datatableaction persisted column filters on this page. */
export function tableFilterStorageKey(pageDetails = PAGE_DETAILS) {
  const pageName = pageDetails?.pagename || 'unknown';
  const scopeIdentifier = pageDetails?.cycle_id || 'global';
  return `dt_filter:${pageName}:${scopeIdentifier}`;
}

/** Clear persisted table filters when the datatable is not mounted (e.g. filter-empty state). */
export function clearPersistedTableFilters(store, pageDetails = PAGE_DETAILS) {
  if (!store?.commit || !pageDetails?.pagename) return;
  store.commit('clearTableFilter', tableFilterStorageKey(pageDetails));
}

export function emptyUserForm() {
  return {
    _id: null,
    user_id: null,
    name: '',
    email: '',
    role: 'general',
    role_label: 'general',
    location_ids: [],
    status: true,
    isFbcAdmin: false,
  };
}

/**
 * Normalize legacy stored role values (e.g. "FBC Admin") to current codes.
 */
function normalizeRole(role) {
  const raw = String(role || '').trim();
  if (!raw) return '';
  if (raw === 'FBC Admin' || raw === 'FBCAdmin') return 'fbc_admin';
  return raw;
}

export function buildFormFromRow(row = {}) {
  let role = normalizeRole(row.role);
  let role_label = String(row.role_label || '').trim();
  const isFbcAdmin = row.isFbcAdmin === true;

  if (!role && role_label) {
    role = ROLE_BY_ROLE_LABEL[role_label] || '';
  }
  if (!role_label && role) {
    role_label = ROLE_LABEL_BY_ROLE[role] || '';
  }

  // TODO(phase-2): Do not force Controller from platform FBC Admin. The backend
  // hack in roleSync.js mirrors the two until one role model exists.

  return {
    _id: row._id || null,
    user_id: row.user_id || null,
    name: row.name || '',
    email: row.email || '',
    role,
    role_label,
    location_ids: Array.isArray(row.location_ids)
      ? row.location_ids.map((id) => String(id))
      : [],
    status: row.status === true,
    isFbcAdmin,
  };
}

export function roleLabelForRole(role) {
  return ROLE_LABEL_BY_ROLE[normalizeRole(role)] || '';
}

function capitalizeFirst(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Display text from role_label (preferred). Falls back to role option / locked pair.
 * Returns empty string when neither is present (no L3 row yet).
 * Always surfaces role_label (Controller / Manager / General), never "FBC Admin".
 */
export function getRoleDisplayName(role_label, role) {
  const label = String(role_label || '').trim();

  if (label && ROLE_DISPLAY_BY_LABEL[label]) {
    return ROLE_DISPLAY_BY_LABEL[label];
  }

  if (label) return capitalizeFirst(label);

  const normalized = normalizeRole(role);

  if (normalized) {
    const match = ROLE_OPTIONS.find((opt) => opt.value === normalized);

    if (match?.role_label) {
      return (
        ROLE_DISPLAY_BY_LABEL[match.role_label]
        || capitalizeFirst(match.role_label)
      );
    }

    const derivedLabel = ROLE_LABEL_BY_ROLE[normalized];

    if (derivedLabel && ROLE_DISPLAY_BY_LABEL[derivedLabel]) {
      return ROLE_DISPLAY_BY_LABEL[derivedLabel];
    }

    if (derivedLabel) return capitalizeFirst(derivedLabel);
  }

  return '';
}

export function getMemberInitials(name) {
  const clean = String(name || '').trim();
  const parts = clean.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }

  return (clean.slice(0, 2) || '?').toUpperCase();
}

/**
 * True when every location id in the full catalog is present in the user's
 * location_ids (enabled and not enabled).
 */
export function hasAllLocations(locationIds = [], allLocations = []) {
  const catalogIds = (allLocations || [])
    .map((loc) => String(loc?._id ?? loc?.id ?? loc ?? ''))
    .filter(Boolean);

  if (!catalogIds.length) return false;

  const selected = new Set(
    (locationIds || []).map((id) => String(id)).filter(Boolean),
  );

  return catalogIds.every((id) => selected.has(id));
}

/**
 * Build id → name map from the full locations catalog (drawer / table source).
 * Prefers catalog `name`; falls back to id only when the id is absent from the list.
 */
export function buildLocationNameById(allLocations = []) {
  const map = new Map();
  (allLocations || []).forEach((loc) => {
    const id = String(loc?._id ?? loc?.id ?? '');
    if (!id) return;
    const name = String(loc?.name || loc?.label || '').trim();
    map.set(id, name || id);
  });
  return map;
}

/**
 * Resolve display names for location_ids via the full location catalog.
 * Does not read API `locations` name objects. Unknown ids: skip when a
 * catalog exists; otherwise fall back to the raw id.
 */
export function resolveLocationNames(locationIds = [], allLocations = []) {
  const nameById = buildLocationNameById(allLocations);
  const hasCatalog = nameById.size > 0;

  return (locationIds || [])
    .map((id) => String(id))
    .filter(Boolean)
    .map((id) => {
      if (nameById.has(id)) return nameById.get(id);
      return hasCatalog ? null : id;
    })
    .filter(Boolean);
}

/**
 * Shape list API rows for the table (location chips + overflow).
 * Role display is driven by role_label; missing L3 role renders empty.
 * Location labels come from location_ids + allLocations (same list as
 * the edit drawer), never from API row.locations.
 * When every catalog location is assigned, location cell shows "All".
 *
 * `locationDisplay` is an array of location names so ColumnFilter (and
 * filterByDynamicCriteria) can treat each name as its own option, same as
 * Chart of Account `location`. Empty array → ColumnFilter "(Blank)".
 */
export function formatUserRows(rows = [], allLocations = []) {
  return (rows || []).map((row) => {
    const locationIds = Array.isArray(row.location_ids)
      ? row.location_ids.map((id) => String(id))
      : [];
    const normalizedRole = normalizeRole(row.role);
    const roleLabel = String(row.role_label || '').trim();
    const isController =
      normalizedRole === 'fbc_admin' || roleLabel === 'controller';
    const names = resolveLocationNames(locationIds, allLocations);
    const locationAll =
      isController || hasAllLocations(locationIds, allLocations);
    const primary = locationAll ? 'All' : (names[0] || '—');
    const overflowItems = locationAll ? [] : names.slice(1);
    const roleDisplay = getRoleDisplayName(row.role_label, row.role);
    const locationDisplay = locationAll ? ['All'] : names.slice();

    return {
      ...row,
      _id: row._id,
      name: row.name || '',
      email: row.email || '',
      role: normalizedRole || '',
      role_label: row.role_label || '',
      roleDisplay,
      location_ids: locationIds,
      locationNames: names,
      locationAll,
      locationPrimary: primary,
      locationOverflowCount: overflowItems.length,
      locationOverflowItems: overflowItems,
      locationDisplay,
      status: row.status === true,
      statusDisplay: row.status === true ? 'Active' : 'Inactive',
      isFbcAdmin: row.isFbcAdmin === true,
    };
  });
}

/**
 * Format edit/save API errors for the user form dialog.
 * Prefers structured location_conflicts; falls back to API message string.
 */
export function formatUserManagementSaveError(err, allLocations = []) {
  const response = err?.response?.data;
  const message = String(response?.message || err?.message || '').trim();
  const conflicts = response?.data?.location_conflicts;

  if (Array.isArray(conflicts) && conflicts.length) {
    const nameById = buildLocationNameById(allLocations);
    return conflicts
      .map((conflict) => {
        const locationId = String(conflict?.location_id || '').trim();
        const locationName =
          String(conflict?.location_name || '').trim()
          || (locationId ? nameById.get(locationId) : '')
          || locationId;
        const refs = (conflict?.refs || [])
          .map((ref) => String(ref).trim())
          .filter(Boolean)
          .join(', ');
        if (!locationName || !refs) return '';
        return `Cannot remove this user from ${locationName} because they are assigned as preparer or approver on tasks ${refs}.`;
      })
      .filter(Boolean)
      .join('\n');
  }

  return message || 'Failed to save user.';
}

export function validateUserForm(form = {}, { mode = 'edit' } = {}) {
  const errors = {};

  if (mode === 'add' && !form.user_id) {
    errors.user_id = 'User is required';
  }

  const role = normalizeRole(form.role);

  // TODO(phase-2): Controller is no longer limited to platform FBC Admin.
  // roleSync.js mirrors the two until one role model exists.
  if (!role || !ROLE_LABEL_BY_ROLE[role]) {
    errors.role = 'Role is required';
  }

  if (!Array.isArray(form.location_ids)) {
    errors.location_ids = 'Locations are required';
  }

  return errors;
}
